import { css } from "@linaria/core"
import { Box, CircularProgress, Link, Stack, Tooltip, Typography } from "@mui/material"
import { Check, XClose } from "@untitled-ui/icons-react"

import {
  Activity,
  ActivityAction,
  BurnErrorOrderInfo,
  LimitOrderErrorInfo,
  LimitOrderInfo,
  ProvideOrderErrorInfo,
  ProvideOrderInfo,
  StopLossOrderInfo,
  SwapOrderErrorInfo,
  SwapOrderInfo,
} from "../../ao/activity-api"
import { useTokenInfo } from "../../hooks/use-token-info"
import { PRICE_DECIMALS } from "../../settings"
import { NumberFont } from "../../theme"
import { parseBigIntAsNumber, formatRelative, formatNumberAuto } from "../../utils/format"
import TokenLogo from "../TokenLogo"
import { TokenPill } from "../TokenPill"

const TITLE_MAP: Record<ActivityAction, string> = {
  "Order-Confirmation": "Swap",
  "Order-Error": "Swap",
  "Burn-Confirmation": "Withdrawal",
  "Burn-Error": "Withdrawal",
  "Provide-Confirmation": "Provide",
  "Provide-Error": "Provide",
  "Limit-Order-Confirmation": "Limit Order",
  "Limit-Order-Error": "Limit Order",
  "DCA-Order-Confirmation": "DCA Order",
  "DCA-Order-Error": "DCA Order",
  "Add-Pool-Confirmation": "Create Pool",
  "Add-Pool-Error": "Create Pool",
  "Stop-Order-Confirmation": "Stop Order",
  "Stop-Error-Confirmation": "Stop Order",
  "Multi-Hop-Swap-Success-Notice": "Multi-hop Swap",
  "Multi-Hop-Swap-Cancel-Notice": "Multi-hop Swap",
}

type ActivityRowProps = {
  activity: Activity
}

export function ActivityRow(props: ActivityRowProps) {
  const { activity } = props

  const { txId, action, timestamp, errorMessage, orderInfo } = activity

  const filledPercentage = parseFloat((orderInfo as LimitOrderInfo).filledPercentage)
  const incomplete =
    !isNaN(filledPercentage) && filledPercentage > 0 && filledPercentage < 100 && errorMessage

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        alignItems: "flex-start",
        gap: 1,
      }}
      data-testid="activityRow"
    >
      <Stack sx={{ flexShrink: 0 }}>
        <Stack direction="row" alignItems="center" gap={1}>
          <Typography variant="subtitle1" fontWeight={500} data-testid="activityTitle">
            {TITLE_MAP[action]}
          </Typography>
          <Tooltip
            title={
              incomplete
                ? `Partial fill: ${filledPercentage}%`
                : errorMessage
                  ? `Error: ${errorMessage}`
                  : "Success"
            }
          >
            <Stack
              alignItems="center"
              justifyContent="center"
              sx={{
                borderRadius: 0.5,
                height: 28,
                width: 28,
                background: !errorMessage
                  ? "rgba(var(--mui-palette-success-mainChannel) / 0.1)"
                  : incomplete
                    ? "rgba(var(--mui-palette-warning-mainChannel) / 0.1)"
                    : "rgba(var(--mui-palette-error-mainChannel) / 0.1)",
                "& svg": {
                  width: "0.85rem",
                  height: "0.85rem",
                },
              }}
              data-testid="activityStatus"
            >
              {!errorMessage ? (
                <Check style={{ color: "var(--mui-palette-accent-main)" }} />
              ) : incomplete ? (
                <Box
                  sx={{
                    position: "relative",
                    height: "fit-content",
                    transform: "translate(1px, 1px)",
                  }}
                >
                  <CircularProgress
                    variant="determinate"
                    color="warning"
                    sx={{
                      opacity: 0.2,
                    }}
                    size={16}
                    thickness={5}
                    value={100}
                  />
                  <CircularProgress
                    variant="determinate"
                    color="warning"
                    value={filledPercentage}
                    sx={{
                      position: "absolute",
                      top: 1,
                      left: 0,
                    }}
                    size={16}
                    thickness={5}
                  />
                </Box>
              ) : (
                <XClose style={{ color: "var(--mui-palette-error-main)" }} />
              )}
            </Stack>
          </Tooltip>
        </Stack>
        {action.includes("Limit") && (
          <FilledPercentageExtraDetails orderInfo={orderInfo as LimitOrderInfo | undefined} />
        )}
        {action.includes("DCA") && (
          <FilledPercentageExtraDetails orderInfo={orderInfo as LimitOrderInfo | undefined} />
        )}
        {action.includes("Stop") && (
          <StopTypeExtraDetails orderInfo={orderInfo as StopLossOrderInfo | undefined} />
        )}
        {action.includes("Stop") && (orderInfo as StopLossOrderInfo)?.variant === "limit" && (
          <FilledPercentageExtraDetails orderInfo={orderInfo as LimitOrderInfo | undefined} />
        )}
        <Typography variant="caption" color="secondary" data-testid="activityTimestamp">
          {formatRelative(timestamp * 1000)}
        </Typography>
      </Stack>
      <Stack alignItems="flex-end" sx={{ overflow: "hidden", whiteSpace: "nowrap" }}>
        {action === "Burn-Error" ? (
          <BurnErrorActivityDetails orderInfo={orderInfo as BurnErrorOrderInfo | undefined} />
        ) : action.includes("Add-Pool") ? (
          <AddPoolActivityDetails orderInfo={orderInfo as ProvideOrderInfo | undefined} />
        ) : action.includes("Provide") || action.includes("Burn") ? (
          <ProvideActivityDetails orderInfo={orderInfo as ProvideOrderInfo | undefined} />
        ) : action.includes("Limit") ? (
          <LimitOrderActivityDetails orderInfo={orderInfo as LimitOrderInfo | undefined} />
        ) : action.includes("DCA") ? (
          <LimitOrderActivityDetails orderInfo={orderInfo as LimitOrderInfo | undefined} />
        ) : action.includes("Stop") ? (
          <LimitOrderActivityDetails orderInfo={orderInfo as LimitOrderInfo | undefined} />
        ) : (
          <SwapActivityDetails orderInfo={orderInfo as SwapOrderInfo | undefined} />
        )}
        <Typography color="secondary">
          {activity.agentId && (
            <>
              <Link
                href={`https://dexi.arweave.net/#/entity/${activity.agentId}`}
                target="_blank"
                rel="noopener noreferrer"
                color="secondary"
                variant="caption"
                data-testid="dexiLink"
              >
                View on Dexi
              </Link>{" "}
              •{" "}
            </>
          )}
          <Link
            href={`https://ao.link/#/message/${txId}`}
            target="_blank"
            rel="noopener noreferrer"
            color="secondary"
            variant="caption"
            data-testid="aoLink"
          >
            View on ao.link
          </Link>
        </Typography>
      </Stack>
    </Box>
  )
}

function SwapActivityDetails({ orderInfo }: { orderInfo?: SwapOrderInfo | SwapOrderErrorInfo }) {
  const fromTokenId = orderInfo?.fromToken
  const toTokenId = orderInfo?.toToken
  const fromAmount = orderInfo?.fromQuantity
  const toAmount = orderInfo?.toQuantity

  const [fromToken] = useTokenInfo(fromTokenId)
  const [toToken] = useTokenInfo(toTokenId)

  return (
    <>
      {toAmount === undefined || toToken === undefined ? (
        <>N/A</>
      ) : (
        <Tooltip title={parseBigIntAsNumber(BigInt(toAmount), toToken.denomination)}>
          <TokenPill
            className={css`
              font-size: 1rem;
              font-weight: 500;
            `}
            data-testid="swapToPill"
          >
            <Typography
              fontSize="inherit"
              fontFamily={NumberFont}
              fontWeight={500}
              sx={{
                textOverflow: "ellipsis",
                overflow: "hidden",
              }}
              data-testid="toAmount"
            >
              {formatNumberAuto(parseBigIntAsNumber(BigInt(toAmount), toToken.denomination))}
            </Typography>
            <TokenLogo
              tx={toToken.logo}
              alt={`${toToken.name} logo`}
              size="medium"
              data-testid="toLogo"
            />
            <span data-testid="toTicker">{toToken.ticker}</span>
          </TokenPill>
        </Tooltip>
      )}
      {fromAmount === undefined || fromToken === undefined ? (
        <>N/A</>
      ) : (
        <Tooltip title={parseBigIntAsNumber(BigInt(fromAmount), fromToken.denomination)}>
          <Typography
            variant="caption"
            component={Stack}
            direction="row"
            gap={0.5}
            sx={{ maxWidth: "100%" }}
            color="secondary"
            data-testid="swapFromDetails"
          >
            from{" "}
            <Typography
              fontSize="inherit"
              fontFamily={NumberFont}
              fontWeight={500}
              component={"div"}
              sx={{
                textOverflow: "ellipsis",
                overflow: "hidden",
              }}
              data-testid="fromAmount"
            >
              {formatNumberAuto(parseBigIntAsNumber(BigInt(fromAmount), fromToken.denomination))}
            </Typography>{" "}
            <span data-testid="fromTicker">{fromToken.ticker}</span>
          </Typography>
        </Tooltip>
      )}
    </>
  )
}

function ProvideActivityDetails({
  orderInfo,
}: {
  orderInfo?: ProvideOrderInfo | ProvideOrderErrorInfo
}) {
  const tokenAAmount = orderInfo?.tokenAAmount
  const tokenAId = orderInfo?.tokenA
  const tokenBAmount = orderInfo?.tokenBAmount
  const tokenBId = orderInfo?.tokenB

  const [tokenA] = useTokenInfo(tokenAId)
  const [tokenB] = useTokenInfo(tokenBId)

  return (
    <>
      {tokenBAmount === undefined || tokenB === undefined ? (
        <>N/A</>
      ) : (
        <Tooltip title={parseBigIntAsNumber(BigInt(tokenBAmount), tokenB.denomination)}>
          <Typography
            variant="body2"
            component={Stack}
            direction="row"
            gap={0.5}
            sx={{ maxWidth: "100%" }}
          >
            <Typography
              fontSize="inherit"
              fontFamily={NumberFont}
              fontWeight={500}
              sx={{
                textOverflow: "ellipsis",
                overflow: "hidden",
              }}
              data-testid="provideTokenBAmount"
            >
              {formatNumberAuto(parseBigIntAsNumber(BigInt(tokenBAmount), tokenB.denomination))}
            </Typography>
            <TokenLogo tx={tokenB.logo} alt={`${tokenB.name} logo`} size="small" />
            <span data-testid="provideTokenBTicker">{tokenB.ticker}</span>
          </Typography>
        </Tooltip>
      )}
      {tokenAAmount === undefined || tokenA === undefined ? (
        <>N/A</>
      ) : (
        <Tooltip title={parseBigIntAsNumber(BigInt(tokenAAmount), tokenA.denomination)}>
          <Typography
            variant="body2"
            component={Stack}
            direction="row"
            gap={0.5}
            sx={{ maxWidth: "100%" }}
          >
            <Typography
              fontSize="inherit"
              fontFamily={NumberFont}
              fontWeight={500}
              component={"div"}
              sx={{
                textOverflow: "ellipsis",
                overflow: "hidden",
              }}
              data-testid="provideTokenAAmount"
            >
              {formatNumberAuto(parseBigIntAsNumber(BigInt(tokenAAmount), tokenA.denomination))}
            </Typography>{" "}
            <TokenLogo tx={tokenA.logo} alt={`${tokenA.name} logo`} size="small" />
            <span data-testid="provideTokenATicker">{tokenA.ticker}</span>
          </Typography>
        </Tooltip>
      )}
    </>
  )
}

function AddPoolActivityDetails({
  orderInfo,
}: {
  orderInfo?: ProvideOrderInfo | ProvideOrderErrorInfo
}) {
  const tokenAId = orderInfo?.tokenA
  const tokenBId = orderInfo?.tokenB

  const [tokenA] = useTokenInfo(tokenAId)
  const [tokenB] = useTokenInfo(tokenBId)

  return (
    <>
      {tokenB === undefined ? (
        <>N/A</>
      ) : (
        <Typography
          variant="body2"
          component={Stack}
          direction="row"
          gap={0.5}
          sx={{ maxWidth: "100%" }}
        >
          <TokenLogo tx={tokenB.logo} alt={`${tokenB.name} logo`} size="small" />
          <span data-testid="addPoolTokenBTicker">{tokenB.ticker}</span>
        </Typography>
      )}
      {tokenA === undefined ? (
        <>N/A</>
      ) : (
        <Typography
          variant="body2"
          component={Stack}
          direction="row"
          gap={0.5}
          sx={{ maxWidth: "100%" }}
        >
          <TokenLogo tx={tokenA.logo} alt={`${tokenA.name} logo`} size="small" />
          <span data-testid="addPoolTokenATicker">{tokenA.ticker}</span>
        </Typography>
      )}
    </>
  )
}

function LimitOrderActivityDetails({
  orderInfo,
}: {
  orderInfo?: LimitOrderInfo | LimitOrderErrorInfo
}) {
  const fromTokenId = orderInfo?.fromToken
  const toTokenId = orderInfo?.toToken
  let fromAmount = orderInfo ? orderInfo.fromQuantity : undefined
  let toAmount = orderInfo ? orderInfo.toQuantity : undefined
  const filledPrice = orderInfo?.filledPrice === "n/A" ? null : orderInfo?.filledPrice

  const [fromToken] = useTokenInfo(fromTokenId)
  const [toToken] = useTokenInfo(toTokenId)

  const filledPercentage = orderInfo ? parseFloat(orderInfo.filledPercentage) : undefined
  if (filledPercentage && filledPercentage !== 100) {
    fromAmount = fromAmount
      ? String(
          (filledPercentage *
            parseFloat(parseBigIntAsNumber(fromAmount, fromToken?.denomination))) /
            100,
        )
      : undefined
    toAmount = toAmount
      ? String(
          (filledPercentage * parseFloat(parseBigIntAsNumber(toAmount, toToken?.denomination))) /
            100,
        )
      : undefined
  } else {
    fromAmount = fromAmount
      ? parseBigIntAsNumber(BigInt(fromAmount), fromToken?.denomination)
      : undefined
    toAmount = toAmount ? parseBigIntAsNumber(BigInt(toAmount), toToken?.denomination) : undefined
  }

  return (
    <>
      {toAmount === undefined || toToken === undefined ? (
        <>N/A</>
      ) : (
        <Tooltip title={toAmount}>
          <TokenPill
            className={css`
              font-size: 1rem;
              font-weight: 500;
            `}
          >
            <Typography
              fontSize="inherit"
              fontFamily={NumberFont}
              fontWeight={500}
              sx={{
                textOverflow: "ellipsis",
                overflow: "hidden",
              }}
              data-testid="limitOrderToAmount"
            >
              {formatNumberAuto(toAmount)}
            </Typography>
            <TokenLogo tx={toToken.logo} alt={`${toToken.name} logo`} size="medium" />
            <span data-testid="limitOrderToTicker">{toToken.ticker}</span>
          </TokenPill>
        </Tooltip>
      )}
      {fromAmount === undefined || fromToken === undefined ? (
        <>N/A</>
      ) : (
        <Tooltip title={fromAmount}>
          <Typography
            variant="caption"
            component={Stack}
            direction="row"
            gap={0.5}
            sx={{ maxWidth: "100%" }}
            color="secondary"
          >
            from{" "}
            <Typography
              fontSize="inherit"
              fontFamily={NumberFont}
              fontWeight={500}
              component={"div"}
              sx={{
                textOverflow: "ellipsis",
                overflow: "hidden",
              }}
              data-testid="limitOrderFromAmount"
            >
              {formatNumberAuto(fromAmount)}
            </Typography>{" "}
            <span data-testid="limitOrderFromTicker">{fromToken.ticker}</span>
          </Typography>
        </Tooltip>
      )}
      {filledPrice === undefined || fromToken === undefined || toToken === undefined ? (
        <>N/A</>
      ) : (
        <Tooltip
          title={parseBigIntAsNumber(
            BigInt(filledPrice || 0n),
            PRICE_DECIMALS - fromToken.denomination + toToken.denomination,
          )}
        >
          <Typography
            variant="caption"
            component={Stack}
            direction="row"
            gap={0.5}
            sx={{ maxWidth: "100%" }}
            color="secondary"
          >
            Avg. Fill Price{" "}
            <Typography
              color="accent.main"
              fontSize="inherit"
              fontFamily={NumberFont}
              fontWeight={500}
              component={"div"}
              sx={{
                textOverflow: "ellipsis",
                overflow: "hidden",
              }}
              data-testid="limitOrderFillPrice"
            >
              {filledPrice === null
                ? "N/A"
                : formatNumberAuto(
                    parseBigIntAsNumber(
                      BigInt(filledPrice),
                      PRICE_DECIMALS - fromToken.denomination + toToken.denomination,
                    ),
                  )}
            </Typography>{" "}
          </Typography>
        </Tooltip>
      )}
    </>
  )
}

function FilledPercentageExtraDetails({
  orderInfo,
}: {
  orderInfo?: LimitOrderInfo | LimitOrderErrorInfo
}) {
  const filledPercentage = orderInfo?.filledPercentage === "n/A" ? "0" : orderInfo?.filledPercentage

  return (
    <>
      {filledPercentage === undefined ? (
        <>N/A</>
      ) : (
        <Typography
          variant="caption"
          component={Stack}
          direction="row"
          gap={0.5}
          sx={{ maxWidth: "100%" }}
          color="secondary"
        >
          Filled{" "}
          <Typography
            color="accent.main"
            fontSize="inherit"
            fontFamily={NumberFont}
            fontWeight={500}
            component={"div"}
            sx={{
              textOverflow: "ellipsis",
              overflow: "hidden",
            }}
            data-testid="filledPercentage"
          >
            {parseInt(filledPercentage)}/100
          </Typography>{" "}
        </Typography>
      )}
    </>
  )
}

function StopTypeExtraDetails({ orderInfo }: { orderInfo?: StopLossOrderInfo }) {
  const variant = orderInfo?.variant

  return (
    <>
      {variant === undefined ? (
        <>N/A</>
      ) : (
        <Typography
          variant="caption"
          component={Stack}
          direction="row"
          gap={0.5}
          sx={{ maxWidth: "100%", textTransform: "capitalize" }}
          color="secondary"
          data-testid="stopType"
        >
          Type: {variant}
        </Typography>
      )}
    </>
  )
}

function BurnErrorActivityDetails({ orderInfo }: { orderInfo?: BurnErrorOrderInfo }) {
  const burnQuantity = orderInfo?.burnQuantity
  const burnTokenId = orderInfo?.burnToken

  const [burnToken] = useTokenInfo(burnTokenId)

  return (
    <>
      {burnToken === undefined || burnQuantity === undefined ? (
        <>N/A</>
      ) : (
        <Tooltip title={parseBigIntAsNumber(BigInt(burnQuantity), burnToken.denomination)}>
          <Typography
            variant="body2"
            component={Stack}
            direction="row"
            gap={0.5}
            sx={{ maxWidth: "100%", whiteSpace: "nowrap" }}
          >
            <Typography
              fontSize="inherit"
              fontFamily={NumberFont}
              fontWeight={500}
              sx={{
                textOverflow: "ellipsis",
                overflow: "hidden",
              }}
              data-testid="burnQuantity"
            >
              {formatNumberAuto(parseBigIntAsNumber(BigInt(burnQuantity), burnToken.denomination))}
            </Typography>
            <TokenLogo tx={burnToken.logo} alt={`${burnToken.name} logo`} size="small" />
            {/* {burnToken.ticker} */}
            Botega v2 LP
          </Typography>
        </Tooltip>
      )}
    </>
  )
}
